/**
 * Represents the standard JWT payload claims.
 */
export interface JwtPayload {
  exp?: number;
  iat?: number;
  nbf?: number;
  iss?: string;
  sub?: string;
  aud?: string | string[];
  [key: string]: unknown;
}

/**
 * Decodes a base64url-encoded string to a regular string.
 */
function base64UrlDecode(base64Url: string): string {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  return atob(base64 + padding);
}

/**
 * Parses a JWT token and returns the payload without verifying the signature.
 * @param token - The JWT token string
 * @returns The decoded JWT payload or null if the token is invalid
 */
export function parseJwt(token: string): JwtPayload | null {
  if (!token || typeof token !== 'string') {
    return null;
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }

  try {
    const payloadBase64 = parts[1];
    const payloadJson = base64UrlDecode(payloadBase64);
    return JSON.parse(payloadJson) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * Extracts the expiration time from a JWT token.
 * @param token - The JWT token string
 * @returns The expiration time as a Date object, or null if the token is invalid or has no expiration
 */
export function getJwtExpirationTime(token: string): Date | null {
  const payload = parseJwt(token);

  if (!payload || typeof payload.exp !== 'number') {
    return null;
  }

  return new Date(payload.exp * 1000);
}

/**
 * Checks if a JWT token has expired.
 * @param token - The JWT token string
 * @param clockSkewMs - Optional clock skew tolerance in milliseconds (default: 0)
 * @returns true if the token has expired, false if still valid, null if token is invalid
 */
export function isJwtExpired(token: string, clockSkewMs = 0): boolean | null {
  const expirationTime = getJwtExpirationTime(token);

  if (!expirationTime) {
    return null;
  }

  return Date.now() > expirationTime.getTime() + clockSkewMs;
}

/**
 * Returns the remaining time until a JWT token expires.
 * @param token - The JWT token string
 * @returns The remaining time in milliseconds, negative if expired, or null if token is invalid
 */
export function getJwtTimeRemaining(token: string): number | null {
  const expirationTime = getJwtExpirationTime(token);

  if (!expirationTime) {
    return null;
  }

  return expirationTime.getTime() - Date.now();
}
